import type { DrawingBox, DrawingLabel, DrawingLine, OHLCV, PriceLine } from "@luxalgo/vela";
import {
  asBarTimeMs,
  barColumnSpan,
  containingBarIndex,
} from "./tesseraPatternBarHighlight";

export type TesseraConceptOverlayKind = "bar" | "zone" | "level" | "marker" | "ray";

export type TesseraConceptOverlay = {
  id: string;
  label: string;
  kind: TesseraConceptOverlayKind;
  timeMs: number | null;
  fromTimeMs?: number | null;
  toTimeMs?: number | null;
  high: number | null;
  low: number | null;
  price: number | null;
  fromPrice?: number | null;
  toPrice?: number | null;
  /** Default: extend right (channel/trend rays). H&S silhouette uses `none`. */
  lineExtend?: "none" | "right";
  arrowRight?: boolean;
  showLabel?: boolean;
  /** Point mark. Enter is an arrow pinned to the fill price. */
  markerStyle?: "arrowup" | "arrowdown" | "diamond";
  /** Optional Vela pane (price default; tessera-panes / tessera-macd / volume). */
  paneId?: string | null;
};

const PALETTE = [
  { solid: "#38BBD1", fill: "rgba(56, 187, 209, 0.16)" },
  { solid: "#A78BFA", fill: "rgba(167, 139, 250, 0.16)" },
  { solid: "#F59E0B", fill: "rgba(245, 158, 11, 0.16)" },
  { solid: "#06E363", fill: "rgba(6, 227, 99, 0.14)" },
  { solid: "#F472B6", fill: "rgba(244, 114, 182, 0.16)" },
  { solid: "#2DD4BF", fill: "rgba(45, 212, 191, 0.16)" },
] as const;

function paletteAt(index: number): (typeof PALETTE)[number] {
  return PALETTE[index % PALETTE.length]!;
}

const SR_SUPPORT = { solid: "#06E363", fill: "rgba(6, 227, 99, 0.12)" };
const SR_RESIST = { solid: "#E00101", fill: "rgba(224, 1, 1, 0.12)" };
const SR_QUARTILE = { solid: "#F59E0B", fill: "rgba(245, 158, 11, 0.14)" };
const HTF_NEUTRAL = { solid: "#94A3B8", fill: "rgba(148, 163, 184, 0.14)" };

/** Divergence / Enter — bull/long green, bear/short red. */
const DIV_BULL = { solid: "#06E363", fill: "rgba(6, 227, 99, 0.12)" };
const DIV_BEAR = { solid: "#E00101", fill: "rgba(224, 1, 1, 0.12)" };

/** Long / short / bull / bear → green or red. Used for Enter and divergence. */
function directionPalette(
  overlay: TesseraConceptOverlay
): { solid: string; fill: string } | null {
  const id = overlay.id.toLowerCase();
  const label = overlay.label.trim().toLowerCase();
  const blob = `${id} ${label}`;
  if (
    blob.includes("short") ||
    blob.includes("bear") ||
    label.includes("↓")
  ) {
    return DIV_BEAR;
  }
  if (
    blob.includes("long") ||
    blob.includes("bull") ||
    label.includes("↑")
  ) {
    return DIV_BULL;
  }
  return null;
}

function divergencePalette(
  overlay: TesseraConceptOverlay
): { solid: string; fill: string } | null {
  const id = overlay.id.toLowerCase();
  const label = overlay.label.trim().toLowerCase();
  if (
    !id.includes(":div-") &&
    !id.includes("rsi_div") &&
    !id.includes("macd_div") &&
    !id.includes("stoch_div") &&
    !id.includes("divergence") &&
    !label.includes("bull") &&
    !label.includes("bear") &&
    label !== "·" &&
    label !== "osc" &&
    !label.includes("div")
  ) {
    return null;
  }
  return directionPalette(overlay) ?? DIV_BULL;
}

/** Enter markers — always color by trade direction encoded in the drawing id. */
function enterPalette(
  overlay: TesseraConceptOverlay
): { solid: string; fill: string } | null {
  const id = overlay.id.toLowerCase();
  const label = overlay.label.trim().toLowerCase();
  if (label !== "enter" && !/:enter(?:$|:)/.test(id)) return null;
  return directionPalette(overlay);
}
const DONCHIAN = {
  high: { solid: "#0F766E", fill: "rgba(15, 118, 110, 0.10)" },
  mid: { solid: "#94A3B8", fill: "rgba(148, 163, 184, 0.08)" },
  low: { solid: "#64748B", fill: "rgba(100, 116, 139, 0.10)" },
  channel: { solid: "#94A3B8", fill: "rgba(148, 163, 184, 0.12)" },
  break: { solid: "#DB2777", fill: "rgba(219, 39, 119, 0.12)" },
} as const;

function donchianPalette(
  overlay: TesseraConceptOverlay
): { solid: string; fill: string } | null {
  const id = overlay.id.toLowerCase();
  const label = overlay.label.trim().toLowerCase();
  if (!id.includes("donch") && !label.includes("donch")) return null;
  if (id.includes("donch-upper") || label.includes("donch high")) return DONCHIAN.high;
  if (id.includes("donch-lower") || label.includes("donch low")) return DONCHIAN.low;
  if (id.includes("donch-mid") || label.includes("donch mid")) return DONCHIAN.mid;
  // Match break rail/marker only — not the concept id `donchian_break`.
  if (
    id.includes("donch-break") ||
    id.includes("donch-marker:break") ||
    label === "break"
  ) {
    return DONCHIAN.break;
  }
  return DONCHIAN.channel;
}

/** Weinstein stage bands — 1 muted, 2 green, 3 amber, 4 red. */
const STAGE_BANDS: Record<1 | 2 | 3 | 4, { solid: string; fill: string }> = {
  1: { solid: "#94A3B8", fill: "rgba(148, 163, 184, 0.14)" },
  2: { solid: "#06E363", fill: "rgba(6, 227, 99, 0.12)" },
  3: { solid: "#F59E0B", fill: "rgba(245, 158, 11, 0.14)" },
  4: { solid: "#E00101", fill: "rgba(224, 1, 1, 0.12)" },
};

function stageBandPalette(
  overlay: TesseraConceptOverlay
): { solid: string; fill: string } | null {
  const id = overlay.id.toLowerCase();
  if (
    !id.includes("stage-seg:") &&
    !/^stage\s*[1-4]$/i.test(overlay.label.trim())
  ) {
    return null;
  }
  const fromId = id.match(/stage-seg:[^:]+:([1-4])(?::|$)/);
  const fromLabel = overlay.label.match(/stage\s*([1-4])/i);
  const n = Number(fromId?.[1] ?? fromLabel?.[1]);
  if (n === 1 || n === 2 || n === 3 || n === 4) return STAGE_BANDS[n];
  return null;
}

function paletteForOverlay(
  overlay: TesseraConceptOverlay,
  index: number
): (typeof PALETTE)[number] | { solid: string; fill: string } {
  const id = overlay.id.toLowerCase();
  const label = overlay.label.toLowerCase();
  const stageBand = stageBandPalette(overlay);
  if (stageBand) return stageBand;
  const donch = donchianPalette(overlay);
  if (donch) return donch;
  const enter = enterPalette(overlay);
  if (enter) return enter;
  const div = divergencePalette(overlay);
  if (div) return div;
  // HTF bias: green = bull, red = bear, gray = neutral (W↓ / D↑ / D· / Neutral).
  const htfChip = overlay.label.trim();
  if (
    id.includes("htf_bias") ||
    /^[wdm]?bias/.test(label) ||
    /^[wdm][↓↑]$/.test(htfChip) ||
    /^[wdm]·$/i.test(htfChip) ||
    label === "neutral"
  ) {
    if (htfChip.includes("↓") || label.includes("bear") || label.includes("short")) {
      return SR_RESIST;
    }
    if (htfChip.includes("↑") || label.includes("bull") || label.includes("long")) {
      return SR_SUPPORT;
    }
    return HTF_NEUTRAL;
  }
  const sr = id.startsWith("sr:") || label.includes("opposing") || /\b(pdh|pdl|pwh|pwl|pmh|pml)\b/.test(label);
  if (!sr) return paletteAt(index);
  if (label.includes("opposing") || id.includes(":opposing")) return SR_QUARTILE;
  if (/\b(pwl|pdl|pml)\b/.test(label) || id.endsWith(":low")) return SR_SUPPORT;
  if (/\b(pwh|pdh|pmh)\b/.test(label) || id.endsWith(":high")) return SR_RESIST;
  return SR_QUARTILE;
}

function barAt(
  bars: readonly OHLCV[],
  timeMs: number | null
): { bar: OHLCV; index: number; span: { from: number; to: number } } | null {
  if (timeMs == null || !Number.isFinite(timeMs) || !bars.length) return null;
  const index = containingBarIndex(bars, timeMs);
  if (index == null) return null;
  const bar = bars[index];
  const span = barColumnSpan(bars, index);
  if (!bar || !span) return null;
  return { bar, index, span };
}

type BarHit = { bar: OHLCV; index: number; span: { from: number; to: number } };

function barHitAt(bars: readonly OHLCV[], index: number): BarHit | null {
  const bar = bars[index];
  const span = barColumnSpan(bars, index);
  return bar && span ? { bar, index, span } : null;
}

/**
 * Edges of a span on the loaded candles. A span that starts before the first loaded
 * candle (prior-month S/R from the 1st of last month) or ends after the last one is
 * clamped to the loaded window instead of dropped; a span entirely outside it is null.
 * Times inside a gap (weekend) snap inward.
 */
function spanEdges(
  bars: readonly OHLCV[],
  fromTimeMs: number | null | undefined,
  toTimeMs: number | null | undefined
): { left: BarHit; right: BarHit } | null {
  if (
    fromTimeMs == null ||
    toTimeMs == null ||
    !Number.isFinite(fromTimeMs) ||
    !Number.isFinite(toTimeMs) ||
    !bars.length
  ) {
    return null;
  }
  const lo = asBarTimeMs(Math.min(fromTimeMs, toTimeMs));
  const hi = asBarTimeMs(Math.max(fromTimeMs, toTimeMs));
  const exactLeft = barAt(bars, lo);
  const exactRight = barAt(bars, hi);
  let leftIndex = exactLeft?.index ?? -1;
  let rightIndex = exactRight?.index ?? -1;
  if (leftIndex < 0) {
    leftIndex = bars.findIndex((b) => asBarTimeMs(b.time) >= lo);
  }
  if (rightIndex < 0) {
    for (let i = bars.length - 1; i >= 0; i -= 1) {
      if (asBarTimeMs(bars[i]!.time) <= hi) {
        rightIndex = i;
        break;
      }
    }
  }
  if (leftIndex < 0 || rightIndex < 0 || leftIndex > rightIndex) return null;
  const left = barHitAt(bars, leftIndex);
  const right = barHitAt(bars, rightIndex);
  return left && right ? { left, right } : null;
}

/**
 * Hour (or chart bar) that contains the instant: last open at or before it.
 * Nearest-open would put 14:30 / 14:45 on the 15:00 candle.
 */
function barContainingInstant(
  bars: readonly OHLCV[],
  timeMs: number | null
): { bar: OHLCV; index: number; span: { from: number; to: number } } | null {
  if (timeMs == null || !Number.isFinite(timeMs) || !bars.length) return null;
  const target = asBarTimeMs(timeMs);
  let index: number | null = null;
  for (let i = 0; i < bars.length; i += 1) {
    if (asBarTimeMs(bars[i]!.time) <= target) index = i;
  }
  if (index == null) return null;
  const bar = bars[index];
  const span = barColumnSpan(bars, index);
  if (!bar || !span) return null;
  return { bar, index, span };
}

function zoneSpan(
  bars: readonly OHLCV[],
  timeMs: number | null,
  fromTimeMs?: number | null,
  toTimeMs?: number | null,
  barCount = 4
): { from: number; to: number } | null {
  const left = barAt(bars, fromTimeMs ?? timeMs);
  const right = barAt(bars, toTimeMs ?? timeMs);
  if (left && right) {
    return {
      from: Math.min(left.span.from, right.span.from),
      to: Math.max(left.span.to, right.span.to),
    };
  }
  // An edge off the loaded candles (e.g. prior-month range): clamp, don't shrink to a few bars.
  const clamped = spanEdges(bars, fromTimeMs, toTimeMs);
  if (clamped) {
    return {
      from: Math.min(clamped.left.span.from, clamped.right.span.from),
      to: Math.max(clamped.left.span.to, clamped.right.span.to),
    };
  }
  const hit = left ?? right ?? barAt(bars, timeMs);
  if (!hit) return null;
  // Prefer spanning the wall-clock window when only one edge snapped.
  if (fromTimeMs != null && toTimeMs != null && Number.isFinite(fromTimeMs) && Number.isFinite(toTimeMs)) {
    const a = barAt(bars, Math.min(fromTimeMs, toTimeMs));
    const b = barAt(bars, Math.max(fromTimeMs, toTimeMs));
    if (a && b) {
      return {
        from: Math.min(a.span.from, b.span.from),
        to: Math.max(a.span.to, b.span.to),
      };
    }
  }
  const lastIndex = Math.min(bars.length - 1, hit.index + Math.max(1, barCount) - 1);
  const last = barColumnSpan(bars, lastIndex);
  if (!last) return hit.span;
  return { from: hit.span.from, to: last.to };
}

function typicalRangeAround(bars: readonly OHLCV[], index: number): number {
  const lo = Math.max(0, index - 8);
  const hi = Math.min(bars.length - 1, index + 8);
  const ranges: number[] = [];
  for (let i = lo; i <= hi; i += 1) {
    const bar = bars[i];
    if (!bar) continue;
    const range = Math.abs(bar.high - bar.low);
    if (range > 0 && Number.isFinite(range)) ranges.push(range);
  }
  if (!ranges.length) {
    const close = bars[index]?.close ?? bars[0]?.close ?? 1;
    return Math.max(Math.abs(close) * 0.0012, 1e-6);
  }
  ranges.sort((a, b) => a - b);
  return ranges[Math.floor(ranges.length * 0.5)]!;
}

/** Keep the pointer on the drawing; `label_down` already sits the pill just above `y`. */
function labelTimeOnDrawing(
  bars: readonly OHLCV[],
  overlay: TesseraConceptOverlay,
  hit: { index: number; bar: OHLCV } | null
): number | null {
  const onDrawing = barAt(bars, overlay.fromTimeMs ?? overlay.timeMs) ?? hit;
  if (onDrawing) return asBarTimeMs(onDrawing.bar.time);
  const t = overlay.fromTimeMs ?? overlay.timeMs;
  return t != null && Number.isFinite(t) ? t : null;
}

function labelPriceAbove(drawingTop: number, unit: number, stackIndex: number): number {
  // Generous steps — small-end label_down pills are tall in screen space.
  return drawingTop + unit * (0.55 + stackIndex * 1.15);
}

/**
 * Vela `placeModel` remaps every item with `overlay: true` onto the price pane
 * (`item.overlay === true ? "price" : paneId`). Osc/MACD concept dots must stay
 * on tessera-panes — otherwise RSI Y (~30–70) lands on gold and flattens candles.
 */
function conceptForceOverlay(paneId: string | null | undefined): boolean {
  const pane = (paneId || "price").trim() || "price";
  return pane === "price";
}

function conceptLabel(opts: {
  id: string;
  timeMs: number;
  price: number;
  text: string;
  color: string;
  paneId?: string | null;
  style?: DrawingLabel["style"];
}): DrawingLabel {
  const paneId = opts.paneId?.trim() || "price";
  return {
    id: opts.id,
    paneId,
    xloc: "bar_time",
    x: opts.timeMs,
    y: opts.price,
    yloc: "price",
    text: opts.text,
    style: opts.style ?? "label_down",
    color: opts.color,
    textColor: "#fff",
    size: "small",
    textAlign: "center",
    fontFamily: "default",
    overlay: conceptForceOverlay(paneId),
  };
}

function conceptLabelPriority(text: string): number {
  const t = text.trim().toLowerCase();
  if (t === "enter") return 0;
  if (t.startsWith("breakout") || t.startsWith("breakdown")) return 1;
  if (t.startsWith("current")) return 2;
  if (t.startsWith("prior") || t === "base") return 3;
  if (t.startsWith("rvol") || t.startsWith("vol")) return 4;
  if (t.startsWith("sma") || t.startsWith("ema")) return 5;
  if (/^stage\s*[1-4]/.test(t)) return 6;
  return 7;
}

/**
 * Same-candle concept pills must form a vertical ladder when they collide —
 * but keep labels that already sit on distinct prices (e.g. Donch high/low/mid).
 */
export function destackConceptLabels(
  labels: DrawingLabel[],
  bars: readonly OHLCV[],
  minGap: number
): DrawingLabel[] {
  if (labels.length < 2 || !(minGap > 0)) return labels;
  const pills: DrawingLabel[] = [];
  const rest: DrawingLabel[] = [];
  for (const label of labels) {
    if (label.style === "label_down") pills.push(label);
    else rest.push(label);
  }
  if (pills.length < 2) return labels;

  const groups = new Map<number, DrawingLabel[]>();
  for (const label of pills) {
    const x = Number(label.x);
    const idx =
      Number.isFinite(x) && bars.length
        ? containingBarIndex(bars, x) ?? Math.round(x)
        : -1;
    const list = groups.get(idx) ?? [];
    list.push(label);
    groups.set(idx, list);
  }

  const stacked: DrawingLabel[] = [...rest];
  Array.from(groups.values()).forEach((group) => {
    if (group.length === 1) {
      stacked.push(group[0]!);
      return;
    }
    group.sort((a, b) => {
      const dy = Number(a.y) - Number(b.y);
      if (Number.isFinite(dy) && Math.abs(dy) > 1e-9) return dy;
      return conceptLabelPriority(String(a.text)) - conceptLabelPriority(String(b.text));
    });
    const longest = Math.max(...group.map((l) => String(l.text || "").length), 8);
    // Gap is a multiple of the bar range. A fixed price floor (1+) lifts FX labels
    // a full unit above the candles and flattens the price scale.
    const ladderGap = Math.max(minGap * 3.2, minGap * (1.6 + longest / 18));
    // Only nearly-coincident Y counts as a collision — distinct rails stay on their prices.
    const overlapEps = minGap * 0.55;
    const clusters: DrawingLabel[][] = [];
    group.forEach((label) => {
      const y0 = Number(label.y);
      const lastCluster = clusters[clusters.length - 1];
      if (!lastCluster) {
        clusters.push([label]);
        return;
      }
      const prevY = Number(lastCluster[lastCluster.length - 1]?.y);
      if (Number.isFinite(y0) && Number.isFinite(prevY) && y0 - prevY < overlapEps) {
        lastCluster.push(label);
      } else {
        clusters.push([label]);
      }
    });
    clusters.forEach((cluster) => {
      if (cluster.length === 1) {
        stacked.push(cluster[0]!);
        return;
      }
      const ys = cluster.map((l) => Number(l.y)).filter((y) => Number.isFinite(y));
      const baseY = ys.length ? Math.min(...ys) : 0;
      cluster.forEach((label, i) => {
        const y0 = Number(label.y);
        if (!Number.isFinite(y0)) {
          stacked.push(label);
          return;
        }
        stacked.push({ ...label, y: baseY + i * ladderGap });
      });
    });
  });
  return stacked;
}

/** Draw selected concepts around the example bar — labeled, not full-chart fills. */
export function emitTesseraConceptOverlays(
  overlays: readonly TesseraConceptOverlay[] | null | undefined,
  bars: readonly OHLCV[]
): {
  boxes: DrawingBox[];
  labels: DrawingLabel[];
  priceLines: PriceLine[];
  lines: DrawingLine[];
} {
  const boxes: DrawingBox[] = [];
  const labels: DrawingLabel[] = [];
  const priceLines: PriceLine[] = [];
  const lines: DrawingLine[] = [];
  if (!overlays?.length || !bars.length) {
    return { boxes, labels, priceLines, lines };
  }

  const stackByTime = new Map<number, number>();
  const nextStack = (timeMs: number | null): number => {
    const key = timeMs != null && Number.isFinite(timeMs) ? timeMs : -1;
    const n = stackByTime.get(key) ?? 0;
    stackByTime.set(key, n + 1);
    return n;
  };

  overlays.forEach((overlay, index) => {
    const color = paletteForOverlay(overlay, index);
    const isEnterMark = /:enter(?:$|:)/i.test(overlay.id);
    const hit = isEnterMark
      ? barContainingInstant(bars, overlay.timeMs) ?? barAt(bars, overlay.timeMs)
      : barAt(bars, overlay.timeMs);
    const timeMs = hit ? asBarTimeMs(hit.bar.time) : overlay.timeMs;
    const label = overlay.label.trim() || "Concept";
    const showLabel = overlay.showLabel !== false;
    const paneId = overlay.paneId?.trim() || "price";

    const pushLabel = (
      x: number | null,
      price: number,
      pinToPrice = false,
      style?: DrawingLabel["style"]
    ) => {
      if (!showLabel || x == null || !Number.isFinite(x)) return;
      // Volume share-counts must never become price-pane Y. Other panes (RSI) emit
      // their own concept overlays from tessera-panes — allow labels there.
      if (paneId === "volume") return;
      const unit = typicalRangeAround(bars, hit?.index ?? 0);
      // Snap stack key to the containing bar so from/to aliases of the same candle share a ladder.
      const stackKey = barAt(bars, x)?.index ?? x;
      const isPoint =
        style === "circle" ||
        style === "diamond" ||
        style === "square" ||
        style === "arrowup" ||
        style === "arrowdown";
      const labelY = pinToPrice || isPoint
        ? price
        : labelPriceAbove(price, unit, nextStack(stackKey));
      labels.push(
        conceptLabel({
          id: `${overlay.id}-label`,
          timeMs: x,
          price: labelY,
          // Point marks (pivot dots, Enter arrows) carry no pill text.
          text: isPoint ? "" : label.replace(/\s*\n\s*/g, " "),
          color: color.solid,
          paneId,
          style: style ?? "label_down",
        })
      );
    };

    if (overlay.kind === "bar") {
      const span = zoneSpan(bars, overlay.timeMs, overlay.fromTimeMs, overlay.toTimeMs, 1);
      if (span) {
        const top =
          overlay.high != null && Number.isFinite(overlay.high)
            ? overlay.high
            : hit?.bar.high ?? null;
        const bottom =
          overlay.low != null && Number.isFinite(overlay.low)
            ? overlay.low
            : hit?.bar.low ?? null;
        if (top != null && bottom != null && top >= bottom) {
          boxes.push({
            id: `${overlay.id}-bar`,
            paneId: "price",
            xloc: "bar_time",
            left: span.from,
            right: span.to,
            top,
            bottom,
            extend: "none",
            bgColor: color.fill,
            borderColor: color.solid,
            borderWidth: 1,
            borderStyle: "solid",
            text: "",
            textColor: "#111827",
            textSize: "small",
            hAlign: "center",
            vAlign: "center",
            wrap: false,
            fontFamily: "default",
            bold: false,
            italic: false,
            overlay: true,
          });
          pushLabel(labelTimeOnDrawing(bars, overlay, hit), top);
          return;
        }
      }
      if (hit && timeMs != null) {
        pushLabel(timeMs, hit.bar.high);
      }
      return;
    }

    if (overlay.kind === "ray") {
      const y1 = overlay.fromPrice ?? overlay.high ?? overlay.price;
      const y2 = overlay.toPrice ?? overlay.low ?? overlay.price;
      const leftHit = barAt(bars, overlay.fromTimeMs ?? overlay.timeMs);
      const rightHit = barAt(bars, overlay.toTimeMs ?? overlay.timeMs);
      const x1 = leftHit
        ? asBarTimeMs(leftHit.bar.time)
        : overlay.fromTimeMs ?? overlay.timeMs;
      const x2 = rightHit
        ? asBarTimeMs(rightHit.bar.time)
        : overlay.toTimeMs ?? overlay.timeMs;
      if (
        y1 != null &&
        y2 != null &&
        Number.isFinite(y1) &&
        Number.isFinite(y2) &&
        x1 != null &&
        x2 != null &&
        Number.isFinite(x1) &&
        Number.isFinite(x2) &&
        x1 !== x2
      ) {
        lines.push({
          id: `${overlay.id}-ray`,
          paneId,
          xloc: "bar_time",
          x1,
          y1,
          x2,
          y2,
          // Channels/trendlines continue past the last pivot; H&S silhouette stops at endpoints.
          extend: overlay.lineExtend === "none" ? "none" : "right",
          color: color.solid,
          invisible: false,
          width: 2,
          style: "solid",
          arrowLeft: false,
          arrowRight: overlay.arrowRight === true,
          overlay: conceptForceOverlay(paneId),
        });
        pushLabel(x1, Math.max(y1, y2));
        return;
      }
    }

    if (overlay.kind === "zone" && overlay.high != null && overlay.low != null) {
      const span = zoneSpan(bars, overlay.timeMs, overlay.fromTimeMs, overlay.toTimeMs);
      if (span && overlay.high > overlay.low) {
        boxes.push({
          id: `${overlay.id}-box`,
          paneId: "price",
          xloc: "bar_time",
          left: span.from,
          right: span.to,
          top: overlay.high,
          bottom: overlay.low,
          extend: "none",
          bgColor: color.fill,
          borderColor: color.solid,
          borderWidth: 1,
          borderStyle: "solid",
          text: "",
          textColor: "#111827",
          textSize: "small",
          hAlign: "center",
          vAlign: "center",
          wrap: false,
          fontFamily: "default",
          bold: false,
          italic: false,
          overlay: true,
        });
        pushLabel(
          labelTimeOnDrawing(bars, overlay, hit),
          // Setup / stage-band pills sit on the candle, not a detached zone top above price.
          (overlay.id.startsWith("setup-zone:") ||
            overlay.id.includes("stage-seg:")) &&
          hit
            ? hit.bar.high
            : overlay.high
        );
        return;
      }
    }

    if (overlay.kind === "level") {
      const price = overlay.price ?? overlay.high ?? overlay.low;
      if (price != null && Number.isFinite(price)) {
        const spanned = overlay.fromTimeMs != null && overlay.toTimeMs != null;
        if (!spanned && hit && hit.index >= 0 && hit.index < bars.length) {
          // A level from its setup candle forward (~60 candles), not across the whole chart:
          // a chart-wide line read as a stray target line on every other part of the chart.
          const endIdx = Math.min(bars.length - 1, hit.index + 60);
          if (endIdx > hit.index) {
            lines.push({
              id: `${overlay.id}-rail`,
              paneId,
              xloc: "bar_time",
              x1: bars[hit.index]!.time,
              y1: price,
              x2: bars[endIdx]!.time,
              y2: price,
              extend: "none",
              color: color.solid,
              invisible: false,
              width: 1,
              style: "dashed",
              arrowLeft: false,
              arrowRight: false,
              overlay: conceptForceOverlay(paneId),
            });
            pushLabel(labelTimeOnDrawing(bars, overlay, hit), price, true);
            return;
          }
        }
        if (!spanned) {
          // No setup candle on the chart: chart-wide dashed line.
          priceLines.push({
            id: `${overlay.id}-line`,
            paneId: "price",
            price,
            color: color.solid,
            width: 1,
            lineStyle: "dashed",
            title: label,
          });
          pushLabel(labelTimeOnDrawing(bars, overlay, hit), price, true);
          return;
        }
        // Spanned levels (e.g. liq Pool Take→Enter): rail from bar centers — never a fat box.
        // Edges off the loaded candles (prior-month S/R) clamp to the window.
        const edges = spanEdges(bars, overlay.fromTimeMs, overlay.toTimeMs);
        const left = edges?.left ?? null;
        const right = edges?.right ?? null;
        if (left && right) {
          const x1 = (left.span.from + left.span.to) / 2;
          const x2 = (right.span.from + right.span.to) / 2;
          if (x1 !== x2) {
            const isPool =
              overlay.label.trim().toLowerCase() === "pool" ||
              overlay.id.toLowerCase().includes("liq_");
            const isDonchMid =
              overlay.id.toLowerCase().includes("donch-mid") ||
              overlay.label.trim().toLowerCase().includes("donch mid");
            const isDivDash =
              overlay.id.toLowerCase().includes("div-reclaim") ||
              overlay.id.toLowerCase().includes("div-structure");
            lines.push({
              id: `${overlay.id}-rail`,
              paneId,
              xloc: "bar_time",
              x1,
              y1: price,
              x2,
              y2: price,
              extend: "none",
              color: color.solid,
              invisible: false,
              width: isPool ? 3 : isDonchMid || isDivDash ? 1 : 2,
              style: isDonchMid || isDivDash ? "dashed" : "solid",
              arrowLeft: false,
              arrowRight: false,
              overlay: conceptForceOverlay(paneId),
            });
          }
        }
        pushLabel(labelTimeOnDrawing(bars, overlay, hit), price, true);
        return;
      }
    }

    if (hit && timeMs != null) {
      const barVolume =
        hit.bar.volume != null && Number.isFinite(Number(hit.bar.volume))
          ? Number(hit.bar.volume)
          : null;
      const barHigh = hit.bar.high;
      let markerPrice: number =
        overlay.price != null && Number.isFinite(overlay.price)
          ? overlay.price
          : paneId === "volume" && barVolume != null
            ? barVolume
            : barHigh;
      // Tessera concept overlays mount on the price indicator. Vela's placeModel then
      // remaps every label to that pane (item.paneId is ignored) — so a "volume" marker
      // priced at share counts (~10^5–10^7) expands the equity Y-axis and flattens candles.
      // Always clamp volume-magnitude Y onto the candle when we would paint on price.
      if (Number.isFinite(barHigh) && Math.abs(barHigh) > 0) {
        if (Math.abs(markerPrice) > Math.abs(barHigh) * 50 && Math.abs(markerPrice) > 10_000) {
          markerPrice = barHigh;
        }
      } else if (paneId === "volume" && (overlay.price == null || !Number.isFinite(overlay.price))) {
        markerPrice = barVolume ?? barHigh;
      }
      // Prefer host-pane labels for concept tags.
      // Divergence pivots: small circles on the swing — not fat "·" pills.
      const pivotDot = /:div-[po][12]:/.test(overlay.id) || label === "·";
      const enterArrow =
        overlay.markerStyle === "arrowup" ||
        overlay.markerStyle === "arrowdown" ||
        overlay.markerStyle === "diamond";
      pushLabel(
        isEnterMark && hit
          ? asBarTimeMs(hit.bar.time)
          : labelTimeOnDrawing(bars, overlay, hit) ?? timeMs,
        markerPrice,
        true,
        enterArrow ? overlay.markerStyle : pivotDot ? "circle" : "label_down"
      );
    }
  });

  const unit = typicalRangeAround(bars, 0);
  return {
    boxes,
    labels: destackConceptLabels(labels, bars, Math.max(unit * 1.1, 1e-8)),
    priceLines,
    lines,
  };
}
